import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Alert,
  Grid,
  MenuItem,
  Typography
} from '@mui/material';
import axios from 'axios';
import { ROLES } from '../../constants/roles';
import {
  CREW_CLEARANCE_CONFIG,
  crewClearanceToFormValue,
  emptyCrewClearanceFormValues,
  appendCrewClearancePayload
} from '../../utils/crewClearance';
import CrewClearanceFields from './CrewClearanceFields';

const ProfileCompletion = ({ onboardingId, requestedRole, initialValues = {}, onCompleted }) => {
  const wl = initialValues.workLocation || {};
  const clearanceInitial =
    (requestedRole === ROLES.CREW || requestedRole === ROLES.LOAN_PILOT || requestedRole === ROLES.EXTERNAL_PILOT)
      ? CREW_CLEARANCE_CONFIG.reduce((acc, { field }) => {
          acc[field] = crewClearanceToFormValue(initialValues[field]);
          return acc;
        }, {})
      : emptyCrewClearanceFormValues();
  const [formData, setFormData] = useState({
    department: initialValues.department || '',
    phone: initialValues.phone || '',
    gender: initialValues.gender || '',
    workLocation: {
      addressLine1: wl.addressLine1 || '',
      addressLine2: wl.addressLine2 || '',
      city: wl.city || '',
      stateRegion: wl.stateRegion || '',
      postalCode: wl.postalCode || '',
      country: wl.country || ''
    },
    ...clearanceInitial,
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleWorkLocationChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      workLocation: { ...prev.workLocation, [field]: value }
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = {
        department: formData.department,
        phone: formData.phone.trim(),
        remarks: formData.remarks || undefined,
        gender: formData.gender || undefined,
        workLocation: formData.workLocation
      };
      appendCrewClearancePayload(body, requestedRole, formData);
      await axios.put(`/api/users/onboard/${onboardingId}/complete-profile`, body);
      if (onCompleted) onCompleted();
    } catch (err) {
      console.error('Profile completion error:', err);
      setError(
        err.response?.data?.errors?.[0]?.msg
        || err.response?.data?.message
        || 'Failed to complete profile.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} mt={2}>
      <Grid container spacing={2} maxWidth={720}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Gender"
            select
            value={formData.gender}
            onChange={handleChange('gender')}
            fullWidth
          >
            <MenuItem value="">Not set</MenuItem>
            <MenuItem value="MALE">Male</MenuItem>
            <MenuItem value="FEMALE">Female</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </TextField>
        </Grid>
        {(requestedRole === ROLES.CREW || requestedRole === ROLES.LOAN_PILOT || requestedRole === ROLES.EXTERNAL_PILOT) && (
          <CrewClearanceFields formData={formData} onChange={handleChange} />
        )}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">Base work / reporting location</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Address line 1"
            value={formData.workLocation.addressLine1}
            onChange={handleWorkLocationChange('addressLine1')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Address line 2"
            value={formData.workLocation.addressLine2}
            onChange={handleWorkLocationChange('addressLine2')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="City"
            value={formData.workLocation.city}
            onChange={handleWorkLocationChange('city')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="State / region"
            value={formData.workLocation.stateRegion}
            onChange={handleWorkLocationChange('stateRegion')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Postal code"
            value={formData.workLocation.postalCode}
            onChange={handleWorkLocationChange('postalCode')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Country"
            value={formData.workLocation.country}
            onChange={handleWorkLocationChange('country')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Department"
            value={formData.department}
            onChange={handleChange('department')}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Remarks"
            multiline
            minRows={2}
            value={formData.remarks}
            onChange={handleChange('remarks')}
            fullWidth
          />
        </Grid>
      </Grid>
      <Stack spacing={2} mt={2} maxWidth={720}>
        {error && <Alert severity="error">{error}</Alert>}
        <Button type="submit" variant="contained" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save Profile'}
        </Button>
      </Stack>
    </Box>
  );
};

export default ProfileCompletion;
